import { useEffect, useState, useCallback } from "react";
import { Bug, LifeBuoy, CheckCircle2, RotateCcw, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader, Empty } from "@/components/Page";

export function FeedbackPanel({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [kind, setKind] = useState(""); // "" | bug | support

  const load = useCallback(() => {
    setRows(null);
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    if (kind) params.set("kind", kind);
    api.get(`/admin/feedback?${params.toString()}`).then((r) => setRows(r.data.feedback)).catch(() => setRows([]));
  }, [filter, kind]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/feedback/${id}`, { status }); toast.success(status === "resolved" ? "Marked resolved" : "Reopened"); load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this report permanently?")) return;
    try { await api.delete(`/admin/feedback/${id}`); toast.success("Report deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const pill = (active, label, onClick, key) => (
    <button key={key} data-testid={`feedback-filter-${key}`} onClick={onClick}
      className={`px-3 py-1 text-xs uppercase tracking-wide rounded-sm border transition-colors ${active ? "border-amber-500 text-amber-300 bg-amber-950/30" : "border-zinc-800 text-zinc-500 hover:text-amber-300"}`}>{label}</button>
  );

  return (
    <div data-testid="admin-feedback-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {pill(filter === "open", "Open", () => setFilter("open"), "open")}
        {pill(filter === "resolved", "Resolved", () => setFilter("resolved"), "resolved")}
        {pill(filter === "all", "All", () => setFilter("all"), "all")}
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        {pill(kind === "", "Both", () => setKind(""), "kind-both")}
        {pill(kind === "bug", "Bugs", () => setKind("bug"), "kind-bug")}
        {pill(kind === "support", "Support", () => setKind("support"), "kind-support")}
      </div>

      {rows === null ? <Loader /> : rows.length === 0 ? <Empty label="No reports here." /> : (
        <div className="space-y-3">
          {rows.map((f) => {
            const isBug = f.kind === "bug";
            const resolved = f.status === "resolved";
            return (
              <div key={f.id} data-testid={`feedback-row-${f.id}`}
                className={`border bg-zinc-950 p-4 rounded-sm ${resolved ? "border-zinc-800 opacity-70" : isBug ? "border-red-900/40" : "border-amber-900/40"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] uppercase tracking-wide font-medium ${isBug ? "text-red-300 bg-red-950/40 border border-red-800/50" : "text-amber-300 bg-amber-950/40 border border-amber-800/50"}`}>
                      {isBug ? <Bug size={11} /> : <LifeBuoy size={11} />} {isBug ? "Bug" : "Support"}
                    </span>
                    {resolved && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] uppercase tracking-wide text-emerald-300 bg-emerald-950/40 border border-emerald-800/50"><CheckCircle2 size={11} /> Resolved</span>}
                    {f.email_sent === false && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] text-zinc-400 bg-zinc-900 border border-zinc-700" title="Email delivery failed — only stored in dashboard"><Mail size={11} /> No email</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {resolved ? (
                      <button data-testid={`feedback-reopen-${f.id}`} onClick={() => setStatus(f.id, "open")} title="Reopen" className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors"><RotateCcw size={15} /></button>
                    ) : (
                      <button data-testid={`feedback-resolve-${f.id}`} onClick={() => setStatus(f.id, "resolved")} title="Mark resolved" className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors"><CheckCircle2 size={16} /></button>
                    )}
                    <button data-testid={`feedback-delete-${f.id}`} onClick={() => remove(f.id)} title="Delete" className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
                {f.subject && <div className="mt-2 text-zinc-100 font-medium text-sm">{f.subject}</div>}
                <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap break-words">{f.message}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                  <span className="text-zinc-300">{f.user_name || "?"}</span>
                  <a href={`mailto:${f.user_email}`} className="text-amber-400/80 hover:text-amber-300">{f.user_email}</a>
                  <span>· {fmt(f.created_at)}</span>
                  {f.ip && <span>· IP {f.ip}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
